import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { isPast } from "date-fns";
import { ArrowLeft, WifiOff } from "lucide-react";
import { type ReactNode, useRef } from "react";

import { ScheduledQuestionnaireExecution } from "@/components/checklists/ScheduledQuestionnaireExecution";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOfflineScheduledQuestionnaireDetail } from "@/hooks/use-offline-scheduled-questionnaire-detail";
import { formatUkDate, isUkToday, isUkTomorrow } from "@/lib/date-utils";

export const Route = createFileRoute("/_authenticated/checklists/$id")({
  validateSearch: (search: Record<string, unknown>): { mode?: string } => ({
    mode: search.mode ? String(search.mode) : undefined,
  }),
  component: ChecklistDetailPage,
});

function ChecklistDetailPage() {
  const { id: scheduledQuestionnaireId } = useParams({
    from: "/_authenticated/checklists/$id",
  });
  const { mode } = useSearch({ from: "/_authenticated/checklists/$id" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedNurseryId, selectedNursery } = useNursery();

  // Use offline-first hook for fetching questionnaire and answers
  const {
    questionnaire: scheduledQuestionnaire,
    answers: initialAnswers,
    isLoading,
    isOffline,
    error,
    errorStatus,
    refetch: refetchAnswers,
  } = useOfflineScheduledQuestionnaireDetail(scheduledQuestionnaireId);

  // IMPORTANT: Use refs to capture initial valid values.
  // This prevents component unmounting during transient context re-renders
  // where selectedNurseryId or user.uid might temporarily be null.
  const nurseryIdRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  // Capture initial valid values
  if (selectedNurseryId && !nurseryIdRef.current) {
    nurseryIdRef.current = selectedNurseryId;
  }
  if (user?.uid && !userIdRef.current) {
    userIdRef.current = user.uid;
  }

  // Use current values if available, otherwise fall back to refs
  const effectiveNurseryId = selectedNurseryId || nurseryIdRef.current;
  const effectiveUserId = user?.uid || userIdRef.current;

  const handleClose = () => {
    // Go back if there's history, otherwise go to checklists
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/checklists" });
    }
  };

  // Loading state - only show on initial load
  if (isLoading && !scheduledQuestionnaire) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  const errorCard = (message: ReactNode) => (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" className="gap-2" onClick={handleClose}>
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>
      <Card>
        <CardContent className="py-8 text-center">{message}</CardContent>
      </Card>
    </div>
  );

  // Context values flicker null on re-render (hence the refs above) — that is a
  // pending state, not a missing checklist.
  if (!effectiveNurseryId || !effectiveUserId) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (isOffline && !scheduledQuestionnaire) {
    return errorCard(
      <div className="flex flex-col items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
          <WifiOff className="h-6 w-6 text-amber-600" />
        </div>
        <p className="text-muted-foreground">
          You&apos;re offline and this checklist hasn&apos;t been cached yet.
        </p>
        <p className="text-sm text-muted-foreground">
          Please connect to the internet to load this checklist for the first
          time.
        </p>
      </div>,
    );
  }

  if (error) {
    return errorCard(
      <p className="text-muted-foreground">
        {errorStatus === 403
          ? "You don't have access to this checklist."
          : errorStatus === 404
            ? "This checklist no longer exists."
            : "Couldn't load this checklist. Please try again."}
      </p>,
    );
  }

  if (!scheduledQuestionnaire) {
    return errorCard(
      <p className="text-muted-foreground">Checklist not found.</p>,
    );
  }

  // Preview mode: explicitly requested via search param or safety net for future checklists
  const isPreview =
    mode === "preview" ||
    !isPast(new Date(scheduledQuestionnaire.availableFrom));

  const { template } = scheduledQuestionnaire;

  // Map data to the format expected by the execution component
  const questionnaire = {
    id: scheduledQuestionnaire.id,
    templateId: template.id,
    title: template.name,
    description: template.description || null,
    status: scheduledQuestionnaire.status,
    availableUntil: scheduledQuestionnaire.availableUntil,
    missBehaviour: scheduledQuestionnaire.missBehaviour,
    questions: (template.questions || []).map((q) => ({
      id: q.id,
      questionnaireId: scheduledQuestionnaire.id,
      questionText: q.questionText,
      questionType: q.questionType,
      options: q.options,
      isRequired: q.isRequired,
      isHidden: q.isHidden,
      orderIndex: q.orderIndex,
      validationRules: q.validationRules,
    })),
  };

  const dueDate = new Date(scheduledQuestionnaire.availableUntil);
  const dueTime = isUkToday(dueDate)
    ? `Today, ${formatUkDate(dueDate, "h:mm a")}`
    : isUkTomorrow(dueDate)
      ? `Tomorrow, ${formatUkDate(dueDate, "h:mm a")}`
      : formatUkDate(dueDate, "d MMM, h:mm a");

  return (
    <ScheduledQuestionnaireExecution
      key={scheduledQuestionnaire.id} // Stable key prevents unnecessary re-mounts
      scheduledQuestionnaireId={scheduledQuestionnaire.id}
      questionnaire={questionnaire}
      nurseryId={effectiveNurseryId}
      userId={effectiveUserId}
      dueTime={dueTime}
      location={selectedNursery?.name}
      initialAnswers={initialAnswers}
      onClose={handleClose}
      onRefetchAnswers={refetchAnswers}
      isPreview={isPreview}
      needsAcknowledgement={scheduledQuestionnaire.hasUnresolvedFailedCheck}
      failedCheckCount={scheduledQuestionnaire.failedCheckCount}
      acknowledgements={scheduledQuestionnaire.acknowledgements}
    />
  );
}
